import React from 'react';
import { isNil, toNumber } from 'lodash';
import InfoPopupHeaderSetting from '../InfoPopupHeaderSetting';
import * as BuilderInfoUI from '../../BuilderInfoUI';
import { CohortConditionIcon } from '../../../Icon';
import {
  IFunnelCohortFieldsFragment,
  TimeUnit,
  TimeWindowKey,
} from '../../../../utils/apollo/graphqlSchema';
import CohortDetailsDateRange from './CohortDetailsDateRange';
import CohortDetailsDurationStartDate from './CohortDetailsDurationStartDate';
import { formatTimeUnit, getCapitalizedTimeUnitText } from '../../../../utils';
import { parseAliasReplacements } from '../../../../utils/redux/util';
import { getAliasedName } from '../../../../utils/redux/applyAliasReplacements';
import {
  CohortDetailsBorderedInfo,
  CohortDetailsContent,
} from './CohortDetailsUI';
import { assertNotNil } from '../../../../utils/types';

interface IProps {
  cohort: IFunnelCohortFieldsFragment;
  cohortIndex: number;
}

export default ({ cohort, cohortIndex }: IProps) => {
  const {
    type,
    operator,
    timeWindow,
    funnelOperatorValues,
    analysis,
    datePicker,
    subject,
    durationStartDate,
    lookForwardWindow,
  } = cohort;

  const steps = analysis.query.steps;
  const operatorValue = funnelOperatorValues[0];
  const stepIndex = isNaN(operatorValue) ? 0 : toNumber(operatorValue);
  const selectedStep = steps[stepIndex];

  const parsedAliasReplacements = parseAliasReplacements(
    analysis.pref.aliasReplacements,
  );

  const getFunnelCohortRowContent = () => {
    if (cohort.timeToConvert) {
      const isLastStep = stepIndex === steps.length - 1;
      const nextStep = isLastStep ? steps[stepIndex] : steps[stepIndex + 1];
      const startStep = isLastStep ? steps[0] : steps[stepIndex];
      assertNotNil(startStep.seq);
      assertNotNil(nextStep.seq);
      return (
        <>
          <CohortDetailsBorderedInfo>{`${startStep.seq + 1}: ${
            getAliasedName(
              startStep.name,
              startStep.userAlias.aliasId,
              parsedAliasReplacements,
            ).name
          } - ${nextStep.seq + 1}: ${
            getAliasedName(
              nextStep.name,
              nextStep.userAlias.aliasId,
              parsedAliasReplacements,
            ).name
          }`}</CohortDetailsBorderedInfo>
          <CohortDetailsBorderedInfo>{`${
            cohort.timeToConvert.operator.name
          }`}</CohortDetailsBorderedInfo>
          {cohort.timeToConvert.values?.length > 0 && (
            <>
              <CohortDetailsBorderedInfo>
                {cohort.timeToConvert.values[0]}
              </CohortDetailsBorderedInfo>
              {cohort.timeToConvert.values.length === 2 && (
                <>
                  <div>and</div>
                  <CohortDetailsBorderedInfo>
                    {cohort.timeToConvert.values[1]}
                  </CohortDetailsBorderedInfo>
                </>
              )}
            </>
          )}
          <CohortDetailsBorderedInfo>{`${
            getCapitalizedTimeUnitText(cohort.timeToConvert.timeUnit).plural
          }`}</CohortDetailsBorderedInfo>
        </>
      );
    } else {
      return (
        <>
          <CohortDetailsBorderedInfo>{operator.name}</CohortDetailsBorderedInfo>
          <CohortDetailsBorderedInfo>{`${stepIndex + 1}: ${
            getAliasedName(
              selectedStep.name,
              selectedStep.userAlias.aliasId,
              parsedAliasReplacements,
            ).name
          }`}</CohortDetailsBorderedInfo>
        </>
      );
    }
  };

  return (
    <>
      <BuilderInfoUI.HeaderContainer>
        <CohortConditionIcon conditionIndex={cohortIndex} />
        <InfoPopupHeaderSetting label={'Subject'}>
          {subject.name}
        </InfoPopupHeaderSetting>
        <InfoPopupHeaderSetting label={'Type'}>
          {type.name}
        </InfoPopupHeaderSetting>
        <CohortDetailsDateRange datePicker={datePicker} />
        {durationStartDate && (
          <CohortDetailsDurationStartDate
            durationStartDate={durationStartDate}
          />
        )}
      </BuilderInfoUI.HeaderContainer>
      <CohortDetailsContent>
        <BuilderInfoUI.HighlightedLabel>Funnel</BuilderInfoUI.HighlightedLabel>
        <CohortDetailsBorderedInfo>
          <BuilderInfoUI.StyledPopupLabel>
            {analysis.display}
          </BuilderInfoUI.StyledPopupLabel>
        </CohortDetailsBorderedInfo>
        {getFunnelCohortRowContent()}
        {!isNil(lookForwardWindow) && (
          <>
            <div>through {formatTimeUnit(TimeUnit.Day)}</div>
            <CohortDetailsBorderedInfo>
              {lookForwardWindow}
            </CohortDetailsBorderedInfo>
          </>
        )}
        {!cohort.timeToConvert && (
          <CohortDetailsBorderedInfo>
            {timeWindow.name}
          </CohortDetailsBorderedInfo>
        )}
        {timeWindow.key !== TimeWindowKey.Overall && <div>day(s)</div>}
      </CohortDetailsContent>
    </>
  );
};
